import { useEffect, useState } from "react";
import { Minus, Plus, CreditCard, Store, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "../../components/ui/drawer";
import { VegMark } from "../../components/VegMark";
import { api, calcTotals, errMsg, inr, uuid } from "../../lib/api";
import { CUSTOMER_ONLINE_PAYMENTS_ENABLED } from '../../lib/customerPayments';

export async function flushPending(qr) {
  const raw = localStorage.getItem(`mbp_pending_${qr}`);
  if (!raw || !navigator.onLine) return null;
  try {
    const { data } = await api.post("/public/orders", JSON.parse(raw));
    localStorage.removeItem(`mbp_pending_${qr}`);
    localStorage.removeItem(`mbp_idem_${qr}`);
    toast.success("Your saved order was sent");
    return data;
  } catch (e) {
    if (e.response) { localStorage.removeItem(`mbp_pending_${qr}`); toast.error(errMsg(e)); }
    return null;
  }
}

export default function CartSheet({ open, onClose, cart, setCart, menu, qr, onPlaced }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState(CUSTOMER_ONLINE_PAYMENTS_ENABLED ? 'online' : 'counter');
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState(null);
  const [code, setCode] = useState("");
  const [phoneToken, setPhoneToken] = useState(null);
  useEffect(()=>{
    if(!open||typeof api.get!=='function')return;
    let active=true;
    api.get('/customer/me').then(({data})=>{if(active&&data.customer){setName(data.customer.name||'');setPhone(data.customer.phone);setPhoneToken(data.customer.phone_token);setOtp(null);}}).catch(()=>{});
    return()=>{active=false;};
  },[open]);
  const [couponCode,setCouponCode]=useState(''),[couponQuote,setCouponQuote]=useState(null),[couponBusy,setCouponBusy]=useState(false),[couponError,setCouponError]=useState('');
  const sendOtp = async () => {
    try { const { data } = await api.post("/public/otp/send", { qr_token: qr, phone }); setOtp(data); toast.success(`Code sent by SMS (test mode code: ${data.dev_code})`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const verifyOtp = async () => {
    try { const { data } = await api.post("/public/otp/verify", { qr_token: qr, phone, code }); setPhoneToken(data.phone_token); setOtp(null); toast.success("Phone verified, you'll earn loyalty points"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const tp = menu.outlet.tax_profile;
  const lines = cart.map(c=>({item_id:c.item_id,qty:c.qty,modifier_ids:c.modifier_ids,notes:c.notes}));
  const cartSignature=JSON.stringify({qr,lines});
  const coupon = couponQuote?.signature===cartSignature ? couponQuote : null;
  const t = coupon ? {subtotal:coupon.subtotal_paise,service:coupon.service_charge_paise,cgst:coupon.cgst_paise,sgst:coupon.sgst_paise,total:coupon.total_paise} : calcTotals(cart.reduce((a, c) => a + c.unit * c.qty, 0), tp);
  const applyCoupon=async()=>{
    if(!couponCode.trim()||couponBusy)return;
    setCouponBusy(true);setCouponError('');
    try{const {data}=await api.post(`/public/menu/${qr}/coupon`,{code:couponCode.trim().toUpperCase(),lines});setCouponQuote({...data,signature:cartSignature});setCouponCode(data.coupon.code);toast.success('Coupon applied');}
    catch(e){setCouponQuote(null);setCouponError(errMsg(e));}finally{setCouponBusy(false);}
  };
  const setQty = (key, d) => setCart((c) => c.map((x) => (x.key === key ? { ...x, qty: x.qty + d } : x)).filter((x) => x.qty > 0));
  const phoneDigits = phone.replace(/\D/g, '');
  const normalizedPhone = phoneDigits.length === 12 && phoneDigits.startsWith('91') ? phoneDigits.slice(2) : phoneDigits;
  const phoneBad = normalizedPhone.length !== 10;
  const detailsMissing = !name.trim() || phoneBad;

  const place = async () => {
    if (busy || couponBusy) return;
    if (detailsMissing) { toast.error('Enter your name and a valid 10-digit phone number.'); return; }
    setBusy(true);
    const ik = `mbp_idem_${qr}`;
    const key = localStorage.getItem(ik) || uuid();
    localStorage.setItem(ik, key);
    const payload = {
      qr_token: qr, idempotency_key: key, payment_mode: mode, name: name.trim(), phone: phoneToken ? null : normalizedPhone, phone_token: phoneToken, notes,
      coupon_code: coupon?.coupon.code || null,
      client_ts: new Date().toISOString(), lines: cart.map((c) => ({ item_id: c.item_id, qty: c.qty, modifier_ids: c.modifier_ids, notes: c.notes })),
    };
    try {
      const { data } = await api.post("/public/orders", payload);
      localStorage.removeItem(ik);
      onPlaced(data);
    } catch (e) {
      if (e.response) { localStorage.removeItem(ik); toast.error(errMsg(e)); }
      else { localStorage.setItem(`mbp_pending_${qr}`, JSON.stringify(payload)); toast("You're offline. Order saved and will be sent automatically when you reconnect."); onClose(); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="mx-auto max-h-[94vh] max-w-md bg-[#FAF6F0]" data-testid="cart-sheet">
        <DrawerDescription className="sr-only">Review your items, enter customer details and choose how to pay.</DrawerDescription>
        <div className="overflow-y-auto px-5 pb-4">
          <div className="mt-2 flex items-center justify-between gap-3"><DrawerTitle className="text-2xl font-bold">Your order</DrawerTitle><button type="button" disabled={busy} onClick={onClose} aria-label="Close cart" className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-stone-200"><X size={20}/></button></div>
          <p className="text-sm text-stone-500">{menu.table.kind} {menu.table.code} · {menu.outlet.name}</p>
          <div className="mt-4 divide-y divide-stone-200 rounded-2xl border border-stone-200 bg-white">
            {cart.map((c) => (
              <div key={c.key} className="flex items-start gap-3 p-3" data-testid={`cart-line-${c.item_id}`}>
                <VegMark flag={c.veg_flag} size={12} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-tight">{c.name}</p>
                  {c.mods.length > 0 && <p className="text-xs text-stone-500">{c.mods.join(", ")}</p>}
                  {c.notes && <p className="text-xs italic text-stone-500">“{c.notes}”</p>}
                  <p className="mt-1 text-sm font-bold">{inr(c.unit * c.qty)}</p>
                </div>
                <div className="flex items-center rounded-lg border border-[#FF9D05] text-[#FF9D05]">
                  <button data-testid={`cart-line-decrease-${c.key}`} onClick={() => setQty(c.key, -1)} className="p-2"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-5 text-center text-sm font-bold">{c.qty}</span>
                  <button data-testid={`cart-line-increase-${c.key}`} onClick={() => setQty(c.key, 1)} className="p-2"><Plus className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            {cart.length === 0 && <p className="p-6 text-center text-sm text-stone-500">Your cart is empty.</p>}
          </div>

          <div className="mt-4 space-y-1.5 rounded-2xl border border-stone-200 bg-white p-4 text-sm" data-testid="cart-totals">
            <Row l="Subtotal" v={inr(t.subtotal)} />
            {coupon && <Row l={`Coupon ${coupon.coupon.code}`} v={`− ${inr(coupon.discount_paise)}`} />}
            {t.service > 0 && <Row l="Service charge" v={inr(t.service)} />}
            <Row l={`CGST ${(tp.cgst_bps || 0) / 100}%`} v={inr(t.cgst)} />
            <Row l={`SGST ${(tp.sgst_bps || 0) / 100}%`} v={inr(t.sgst)} />
            <div className="flex justify-between border-t border-dashed border-stone-300 pt-2 text-base font-bold"><span>Total</span><span data-testid="cart-total">{inr(t.total)}</span></div>
          </div>

          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-3">
            <label htmlFor="checkout-coupon" className="text-sm font-semibold">Have a coupon?</label>
            <div className="mt-2 flex gap-2"><input id="checkout-coupon" data-testid="cart-coupon-input" maxLength={24} value={couponCode} disabled={couponBusy||busy} onChange={e=>{setCouponCode(e.target.value.toUpperCase());setCouponQuote(null);setCouponError('');}} placeholder="Enter coupon code" className="min-w-0 flex-1 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm uppercase" />
              <button type="button" data-testid="cart-coupon-apply" disabled={!cart.length||!couponCode.trim()||couponBusy||busy} onClick={applyCoupon} className="min-h-11 rounded-lg bg-stone-900 px-4 text-sm font-semibold text-white disabled:opacity-40">{couponBusy?'Checking…':'Apply'}</button></div>
            {couponError&&<p role="alert" className="mt-2 text-xs text-red-600">{couponError}</p>}
            {coupon&&<p className="mt-2 flex items-center justify-between gap-2 text-xs text-emerald-700">You save {inr(coupon.discount_paise)}<button type="button" disabled={busy} onClick={()=>{setCouponQuote(null);setCouponCode('');}} className="min-h-9 px-2 underline">Remove</button></p>}
            {couponQuote&&!coupon&&<p className="mt-2 text-xs text-amber-700">Your cart changed. Apply the coupon again to update your discount.</p>}
          </div>

          <label htmlFor="cart-customer-name" className="mt-4 block text-sm font-semibold">Your name <span className="text-red-600">*</span></label>
          <input id="cart-customer-name" data-testid="cart-name-input" required autoComplete="name" maxLength={60} value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" className="mt-2 w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF9D05]" />
          <label htmlFor="cart-customer-phone" className="mt-3 block text-sm font-semibold">Phone number <span className="text-red-600">*</span></label>
          <input id="cart-customer-phone" data-testid="cart-phone-input" required type="tel" autoComplete="tel" maxLength={20} inputMode="tel" value={phone} onChange={(e) => { setPhone(e.target.value.replace(/[^\d+ ]/g, "")); setPhoneToken(null); setOtp(null); }}
            placeholder="10-digit mobile number" aria-invalid={!!phone && phoneBad} aria-describedby={phone && phoneBad ? 'cart-phone-error' : undefined} className="mt-2 w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF9D05]" />
          {phone && phoneBad && <p id="cart-phone-error" className="mt-1 text-xs text-red-600" data-testid="cart-phone-error">Enter a 10-digit mobile number</p>}
          {phone && !phoneBad && !phoneToken && !otp && <button data-testid="otp-send-button" onClick={sendOtp} className="mt-2 text-sm font-semibold text-[#FF9D05] underline">Verify by SMS to earn loyalty points</button>}
          {otp && (
            <div className="mt-2 flex gap-2">
              <input data-testid="otp-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="flex-1 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm" />
              <button data-testid="otp-verify-button" onClick={verifyOtp} disabled={code.length !== 6} className="rounded-xl bg-stone-900 px-4 text-sm font-semibold text-white disabled:opacity-40">Verify</button>
            </div>
          )}
          {phoneToken && <p className="mt-1 text-xs font-semibold text-emerald-700" data-testid="otp-verified">Phone verified ✓</p>}
          <input data-testid="cart-notes-input" value={notes} maxLength={300} onChange={(e) => setNotes(e.target.value)} placeholder="Note for the kitchen (optional)"
            className="mt-2 w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF9D05]" />

          <div className={`mt-4 grid gap-2 ${CUSTOMER_ONLINE_PAYMENTS_ENABLED?'grid-cols-2':'grid-cols-1'}`}>
            {[["online", "Pay now", "UPI / card", CreditCard], ["counter", "Pay at counter", "Cash or card", Store]].filter(([k])=>CUSTOMER_ONLINE_PAYMENTS_ENABLED||k==='counter').map(([k, l, s, Icon]) => (
              <button key={k} data-testid={`payment-mode-${k}`} onClick={() => setMode(k)}
                className={`rounded-2xl border-2 p-3 text-left transition-colors ${mode === k ? "border-[#FF9D05] bg-orange-50" : "border-stone-200 bg-white"}`}>
                <Icon className={`h-5 w-5 ${mode === k ? "text-[#FF9D05]" : "text-stone-400"}`} />
                <p className="mt-1 text-sm font-bold">{l}</p>
                <p className="text-xs text-stone-500">{s}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-stone-200 bg-white px-5 py-4">
          {detailsMissing && <p className="mb-2 text-center text-xs text-stone-500">Enter your name and phone number to continue.</p>}
          <button data-testid="place-order-button" disabled={busy || couponBusy || !cart.length || detailsMissing} onClick={place}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF9D05] py-4 font-semibold text-white transition-colors hover:bg-[#E08C00] disabled:bg-stone-300">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "online" ? `Pay ${inr(t.total)}` : `Place order · ${inr(t.total)}`}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

const Row = ({ l, v }) => <div className="flex justify-between text-stone-600"><span>{l}</span><span>{v}</span></div>;
